/**
 * EvidenciaModal — Modal exclusivo para ver la evidencia de check-in de una
 * reserva sin abrir la reserva completa. Se abre al hacer clic en la cámara 📷
 * de la columna "Checkin" de la tabla de reservaciones.
 *
 * Reutiliza el componente `TabEvidencia` (mismo que aparece en el tab
 * "Evidencia" del slide-over completo) — misma UX, incluye botón de re-OCR.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Button,
  Box, Typography, CircularProgress, Snackbar, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import { TabEvidencia } from './ReservaSlideOver';
import { API_BASE, getAuthHeaders } from './shared/format';

export default function EvidenciaModal({ reservaId, codigoReserva, nombreCliente, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState(null);

  const cargar = useCallback(async () => {
    if (!reservaId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/checkin-evidencia`,
        { headers: getAuthHeaders() });
      if (r.ok) setData(await r.json());
    } catch { setData(null); }
    setLoading(false);
  }, [reservaId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Adaptador para el patrón de toast que espera TabEvidencia
  const toast = {
    success: (m) => setToastMsg({ sev: 'success', msg: m }),
    error: (m) => setToastMsg({ sev: 'error', msg: m }),
    info: (m) => setToastMsg({ sev: 'info', msg: m }),
  };

  return (
    <>
      <Dialog open={!!reservaId} onClose={onClose} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
          color: '#fff', p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <PhotoCameraIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
              Evidencia de check-in
            </Typography>
            <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
              {codigoReserva || '—'} · {nombreCliente || ''}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TabEvidencia data={data} onReload={cargar} onToast={toast} />
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={onClose}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toastMsg} autoHideDuration={4000} onClose={() => setToastMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toastMsg?.sev || 'info'} onClose={() => setToastMsg(null)}>
          {toastMsg?.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
